import { useCallback, useEffect, useMemo, useState } from "react";
import { $crud } from "../../utils/CrudFactory";
import { socket } from "../../components/notification/Notifications";
import { enqueueSnackbar } from "notistack";
import { useOutside } from "../useOutside";
import { useNotiNav } from "../../helper/functions";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "../../components/AdminProvider";

export default function useNotifications() {
  const [openNoti, setOpenNoti] = useState(false);
  const [notifications, setNotifications] = useState({ data: [], total: 0 });
  const [tab, setTab] = useState("All");
  const {
    adminData: { data: admin = {} },
    setAdminData,
  } = useAdmin();
  const { handleNotificationNav } = useNotiNav();
  const refe = useOutside(setOpenNoti);
  const navigate = useNavigate();

  const fetchNoti = useCallback(
    async function fetchNoti() {
      const { data } = await $crud.retrieve("admin/notifications", {
        adminId: admin._id,
        skip: notifications.data.length,
        tab,
      });
      setNotifications((notifications) => ({
        data: [...notifications.data, ...data.result],
        total: data.total,
      }));
    },
    [admin._id, notifications.data.length, tab]
  );

  useEffect(() => {
    fetchNoti();

    if (!socket.connected) {
      socket.connect();
    }

    socket.on("connect", () => {
      // console.log("🟢 Connected socket:", socket.id);

      // ✅ Join admin-specific room using admin ID
      socket.emit("joinAdminRoom", { adminId: admin._id });
    });

    socket.on("admin-notification", (newNotif) => {
      enqueueSnackbar("You have new notification!", {
        variant: "info",
        autoHideDuration: 7000,
      });
      setNotifications((notifications) => ({
        data: [newNotif, ...notifications.data],
        total: notifications.total + 1,
      }));
    });

    socket.on("Authlogout", (adminId) => {
      setAdminData({ data: null, token: null });
      navigate("/login", { replace: true });
    });

    return () => {
      socket.off("admin-notification");
      socket.off("Authlogout");
    };
  }, [admin._id, tab, setAdminData, navigate]);

  const unreadcount = useMemo(() => {
    return notifications.data.filter((obj) => !obj.isRead).length;
  }, [notifications.data]);

  async function handleUpdateRead(notId, i) {
    try {
      await $crud.put("admin/notifications/read-status", {
        adminId: admin._id,
        notificationId: notId,
      });
      setNotifications((notifications) => ({
        ...notifications,
        data: (() => {
          notifications.data[i].isRead = true;
          return structuredClone(notifications.data);
        })(),
      }));
    } catch (e) {
      console.error(e);
    }
  }

  async function handleMarkall() {
    if (unreadcount < 1) return;
    try {
      await $crud.put("admin/notifications/read-status", {
        adminId: admin._id,
        markAll: true,
      });
      if (tab === "Unread") {
        setNotifications({ data: [], total: 0 });
        return;
      }
      setNotifications((notifications) => ({
        ...notifications,
        data: notifications.data.map((obj) => {
          if (!obj.isRead) obj.isRead = true;
          return obj;
        }),
      }));
    } catch (e) {
      console.error(e);
    }
  }

  function handleClick(obj, i) {
    handleNotificationNav(obj);
    setOpenNoti(false);
    if (!obj.isRead) handleUpdateRead(obj._id, i);
  }

  function handleChangeTab(newtab) {
    if (tab === newtab) return;
    setTab(newtab);
    setNotifications({ data: [], total: 0 });
  }

  return {
    handleChangeTab,
    handleClick,
    handleMarkall,
    notifications,
    setNotifications,
    openNoti,
    setOpenNoti,
    refe,
    tab,
    setTab,
    unreadcount,
    fetchNoti,
  };
}
